import React, { useEffect, useMemo } from "react";
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useGroupOrder } from "../../services/group-order/group-order.context";
import { buildSearchState } from "../../services/group-order/group-order.utils";
import CatererList from "../caterer-list/caterer-list";
import CatererDetails from "../caterer-details";

// The office uses the normal search. Once the group order has an address and
// date, the list opens pre-filled with them; before that it still opens with
// the group's head count, so the first add doesn't save a head count of 1.
// With a caterer picked, the list redirects to it unless the user asked to
// browse (?browse=1).
export const GroupCatererList = () => {
  const ctx = useGroupOrder();
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const go = ctx.groupOrder;
  const browsing = params.get("browse") === "1";
  const prefill = useMemo(() => buildSearchState(go), [go]);
  const initialState = useMemo(
    () => prefill || { count: go.headCount || go.maxHeadCount },
    [go.headCount, go.maxHeadCount, prefill],
  );
  // Keyed on any state at all, so the replace below runs at most once.
  const needsInitialState = !location.state;

  useEffect(() => {
    if (needsInitialState && (browsing || !go.catererSlug)) {
      navigate(`${location.pathname}${location.search}`, { replace: true, state: initialState });
    }
  }, [
    browsing,
    go.catererSlug,
    initialState,
    location.pathname,
    location.search,
    navigate,
    needsInitialState,
  ]);

  if (go.catererSlug && !browsing) return <Navigate to={go.catererSlug} replace state={prefill} />;
  if (needsInitialState) return null;
  return <CatererList />;
};

// Switching caterers is allowed: the caterer page's own conflict dialog handles a second caterer.
// The rep's notification used to link by caterer id; the caterer page only loads
// the group's details for its own slug, so an id link is sent on to the slug.
export const GroupCatererDetails = () => {
  const ctx = useGroupOrder();
  const { id } = useParams();
  const go = ctx.groupOrder;
  if (go?.catererSlug && go.catererId && id !== go.catererSlug && id === String(go.catererId)) {
    return (
      <Navigate to={`${ctx.listPath}/${go.catererSlug}`} replace state={buildSearchState(go)} />
    );
  }
  return <CatererDetails />;
};
